import React, { useState } from "react";
import { useField } from "formik";
import { Alert, Input, IconButton } from "@material-tailwind/react";
import PasswordEntropy from "./passwordEntropy";

const FormikPasswordStrength = React.forwardRef(function FormikPasswordStrength({
    color = "black",
    label = "Password",
    showStrength = false,
    ...props
}, ref) {
    const [field, meta] = useField(props);
    const [showPassword, setShowPassword] = useState(false);

    const getStrength = (pwd) => {
        if (pwd.length < 8) {
            return {
                label: "Too Short",
                value: 10,
                color: "bg-[#9E1E41]",
                textColor: "text-[#461220]",
            };
        }

        const entropy = PasswordEntropy.calculate(pwd);

        if (entropy > 100)
            return {
                label: "Excellent!",
                value: 100,
                color: "bg-[#748A2B]",
                textColor: "text-[#234311]",
            };
        if (entropy > 80)
            return {
                label: "Great",
                value: 80,
                color: "bg-[#9FBD3C]",
                textColor: "text-[#48561C]",
            };
        if (entropy > 60)
            return {
                label: "Good",
                value: 60,
                color: "bg-cta",
                textColor: "text-primary",
            };
        if (entropy > 40)
            return {
                label: "Average",
                value: 40,
                color: "bg-[#bb5c3f]",
                textColor: "text-[#8C4530]",
            };

        return {
            label: "Weak",
            value: 20,
            color: "bg-[#9E1E41]",
            textColor: "text-[#461220]",
        };
    };
    const strength = getStrength(field.value || "");

    return (
        <div className="space-y-2">
            {/* Password input with eye toggle */}
            <div className="relative w-full">
                <Input
                    {...field}
                    {...props}
                    ref={ref}
                    type={showPassword ? "text" : "password"}
                    label={label}
                    color={color}
                    error={meta.touched && meta.error ? true : undefined}
                    className="!text-black placeholder:!text-black pr-10"
                    labelProps={{
                        className: `!text-black peer-placeholder-shown:!text-black peer-focus:!text-black max-w-full truncate`,
                    }}
                />
                <IconButton
                    variant="text"
                    size="sm"
                    ripple={false}
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="!absolute right-2 top-2 rounded-full"
                >
                    <span className="material-icons text-gray-600">
                        {showPassword ? "visibility_off" : "visibility"}
                    </span>
                </IconButton>
            </div>

            {/* Progress bar with strength word at end */}

            {field.value && showStrength && (
                <div className="flex flex-row items-center">
                    <div className="flex-[9] relative h-2 w-full rounded-full bg-gray-200">
                        <div
                            className={`h-2 rounded-full transition-all duration-300 ${strength.color}`}
                            style={{ width: `${strength.value}%` }}
                        />
                    </div>
                    <div className="flex-[1] text-right">
                        <span
                            className={`text-xs font-medium ${strength.textColor}`}
                        >
                            {strength.label}
                        </span>
                    </div>
                </div>
            )}

            {meta.touched && meta.error && (
                <div className="flex-2">
                    <Alert color="red" className="mt-2 rounded-lg py-1 pl-5">
                        {meta.error}
                    </Alert>
                </div>
            )}
        </div>
    );
});

export default FormikPasswordStrength;
